// Libs
import axios, { AxiosError, AxiosResponse } from 'axios';
import { SWRResponse } from 'swr';
// App
import { SHOP_ADMINISTRATION_SHOPS_URL } from './shop.constants';
import {
  AxiosHookResponse,
  useAxiosPutRequest,
  useAxiosPostRequest,
  useAxiosGetRequest,
} from 'app-shared/lib/api/utils/axios-request.hooks';
import {
  CreateOrUpdateShopParam,
  CreateOrUpdateShopProductCategoryParam,
  CreateOrUpdateShopLoyaltyProgramParam,
  ShopProductCategoryDetails,
  ShopClient,
  UpdateShopClientAddressByIdParam,
  UpdateShopClientAddressParam,
  UpdateShopClientLoyaltyPointsParam,
  UpdateShopOrdersConfigurationParam,
  UpdateShopLogoParam,
  UpdateShopTicketLogoParam,
} from './shop.interfaces';
import { AddressDetails } from 'app-shared/lib/api/user-access/address';
import { useSWRAxiosGetRequest } from 'app-shared/lib/api/utils/swr-axios-request.hooks';
import { ShopClientDetails } from './shop-client-details.interface';
import { ShopClientTopProduct } from './shop-client-top-product.interface';
import { ShopProductSummary } from 'app-shared/lib/api/shop-administration/product';
import useSearch, { SearchHookResponse } from 'app-shared/lib/api/utils/search-hook';
import { escapeApostrophes } from 'app-shared/lib/utils/search.utils';
import { API_COMMON_BASE_URL } from 'app-shared/lib/api/utils/api.constants';

/**
 * Create shop api hook
 */
export const useCreateShop = (): AxiosHookResponse<CreateOrUpdateShopParam, string> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}`;

  return useAxiosPostRequest(url);
};

/**
 * Update shop api hook
 */
export const useUpdateShop = (): AxiosHookResponse<CreateOrUpdateShopParam, string> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}`;

  return useAxiosPutRequest(url);
};

/**
 * Update shop orders configuration
 */
export const updateShopOrdersConfiguration = (
  param: UpdateShopOrdersConfigurationParam
): Promise<AxiosResponse<number>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/orders/configuration`;

  return axios.patch(url, param);
};

/**
 * Update shop logo, a null logo url removes it
 */
export const updateShopLogo = (param: UpdateShopLogoParam): Promise<AxiosResponse<number>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/logo`;

  return axios.patch(url, param);
};

/**
 * Update the logo printed on the shop tickets, a null ticket logo url removes it
 */
export const updateShopTicketLogo = (param: UpdateShopTicketLogoParam): Promise<AxiosResponse<number>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/ticket-logo`;

  return axios.patch(url, param);
};

/**
 * Get shop api hook
 */
export const useGetShop = (id: number): SWRResponse<CreateOrUpdateShopParam, AxiosError> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${id}` : null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Get shop product categories api hook
 */
export const useGetShopProductCategories = (id: number): SWRResponse<ShopProductCategoryDetails[], AxiosError> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${id}/product-categories` : null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Get shop clients api hook
 */
export const useGetShopClients = (id: number): AxiosHookResponse<void, ShopClient[]> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${id}/clients` : null;

  return useAxiosGetRequest(urlOptional);
};

const buildShopClientUrlOptional = (shopId: number, uid: string): string | null => {
  if (!shopId || !uid) {
    return null;
  }

  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/clients/${uid}`;

  return url;
};

/**
 * Get shop client with order statistics api hook
 */
export const useGetShopClient = (shopId: number, uid: string): SWRResponse<ShopClientDetails, AxiosError> => {
  const urlOptional = buildShopClientUrlOptional(shopId, uid);
  const shopClientResponse = useSWRAxiosGetRequest<ShopClientDetails>(urlOptional, { revalidateOnFocus: false });

  return shopClientResponse;
};

const buildShopClientTopProductsUrlOptional = (shopId: number, uid: string): string | null => {
  if (!shopId || !uid) {
    return null;
  }

  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/clients/${uid}/top-products`;

  return url;
};

/**
 * Get shop client most ordered products api hook
 */
export const useGetShopClientTopProducts = (shopId: number, uid: string): SWRResponse<ShopClientTopProduct[], AxiosError> => {
  const urlOptional = buildShopClientTopProductsUrlOptional(shopId, uid);
  const topProductsResponse = useSWRAxiosGetRequest<ShopClientTopProduct[]>(urlOptional, { revalidateOnFocus: false });

  return topProductsResponse;
};

/**
 * Update shop client loyalty points api
 */
export const updateShopClientLoyaltyPoints = (
  param: UpdateShopClientLoyaltyPointsParam
): Promise<AxiosResponse<ShopClient>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${param.shopId}/clients/${param.uid}/loyalty-points`;

  return axios.patch(url, param);
};

/**
 * Update shop client main address api
 */
export const updateShopClientAddress = (param: UpdateShopClientAddressParam): Promise<AxiosResponse<AddressDetails>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${param.shopId}/clients/${param.uid}/address`;

  return axios.patch(url, param);
};

/**
 * Update one shop client address api
 */
export const updateShopClientAddressById = (
  param: UpdateShopClientAddressByIdParam
): Promise<AxiosResponse<AddressDetails>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${param.shopId}/clients/${param.uid}/addresses/${param.addressId}`;

  return axios.patch(url, param);
};

/**
 * Delete one shop client address api
 */
export const deleteShopClientAddress = (shopId: number, uid: string, addressId: number): Promise<AxiosResponse<void>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/clients/${uid}/addresses/${addressId}`;

  return axios.delete(url);
};

/**
 * Get shop loyalty programs api hook
 */
export const useGetShopLoyaltyPrograms = (
  id: number
): SWRResponse<CreateOrUpdateShopLoyaltyProgramParam[], AxiosError> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${id}/loyalty-programs` : null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false });
};

/**
 * Get shop products api hook
 */
export const useGetShopProducts = (id: number): AxiosHookResponse<void, ShopProductSummary[]> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${id}/products` : null;

  return useAxiosGetRequest(urlOptional);
};

/**
 * Search shop products api axios fetcher
 * @param keywords the keywords for search
 * @param id the shop id
 */
export const searchShopProductsFetcher = (
  keywords: string,
  shopId: number,
  shopProductCategoryId?: number
): Promise<AxiosResponse> => {
  const shopProductCategoryIdQueryPart =
    (shopProductCategoryId && `&shopProductCategoryId=${shopProductCategoryId}`) || '';
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/${shopId}/products/search?keywords=${escapeApostrophes(
    keywords
  )}&limit=20${shopProductCategoryIdQueryPart}`;

  return axios.get(url);
};

export const useSearchShopProducts = (): SearchHookResponse<ShopProductSummary> => {
  return useSearch(searchShopProductsFetcher);
};

/**
 * Update shop product categories api hook
 */
export const useSaveShopProductCategories = (): AxiosHookResponse<
  CreateOrUpdateShopProductCategoryParam[],
  ShopProductCategoryDetails[]
> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/product-categories`;

  return useAxiosPutRequest(url);
};

/**
 * Check if shop product category has products api
 */
export const checkIfShopProductCategoriesHasProducts = (id: number): Promise<AxiosResponse<number>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/product-categories/${id}/check`;

  return axios.get(url);
};

/**
 * Delete shop product category api
 */
export const deleteShopProductCategory = (id: number): Promise<AxiosResponse<number>> => {
  const url = `${API_COMMON_BASE_URL}/${SHOP_ADMINISTRATION_SHOPS_URL}/product-categories/${id}`;

  return axios.delete(url);
};
